// --- CSS Imports ---
import '../style.css';
import './css/chat.css'; // Correct path: same level directory

// --- Module Imports ---
import { initAuth, isUserLoggedIn, getSessionToken, signOut } from './js/auth/auth.js';
import { initApi, getSubscriptions, createSubscription, updateSubscription, deleteSubscription as deleteSubscriptionApi, parseImage, parseText, continueParse, syncSubscriptions } from './js/api/api.js';
import { initDB, addOrUpdateSubscription, getAllSubscriptions, getSubscription, deleteSubscription as deleteSubscriptionStorage, getLocalUnsyncedSubscriptions, bulkAddOrUpdateSubscriptions, bulkDeleteSubscriptions } from './js/storage/storage.js';
import { initChat, addChatMessage, disableChatInput, toggleSendButtonLoading, focusChatInput, removePreviewImage, toggleConfirmationArea, isChatAwaitingConfirmation } from './js/ui/chat/chat.js';
import { initCards, renderSubscriptionCards, addSubscriptionCard, removeSubscriptionCard, updateSubscriptionCard, showLoading as showCardsLoading, showError as showCardsError } from './js/ui/cards/cards.js';
import { initForm, showPartialEditForm, hidePartialEditForm } from './js/ui/form/form.js';

// --- Global State (Minimal) ---
let syncIntervalId = null;
const SYNC_INTERVAL_MINUTES = 5;

// --- DOM Element References (Populated in main) ---
let navButtons = {};
let views = {};
let cardsContainer = null;
let cardsLoadingPlaceholder = null;
let cardsErrorPlaceholder = null;
let cardsEmptyPlaceholder = null;
let chatMessagesElement = null;

// --- View Switching Logic ---

/**
 * Switches the currently visible view.
 * @param {string} viewId - The ID of the view to switch to ('chat', 'list', 'stats').
 */
function switchToView(viewId) {
    console.log(`[Main] Switching to view: ${viewId}`);
    Object.values(views).forEach(view => view.classList.remove('active'));
    Object.values(navButtons).forEach(button => button.classList.remove('active'));

    const activeView = views[viewId];
    const activeButton = navButtons[viewId];

    if (activeView) {
        activeView.classList.add('active');
    } else {
        console.warn(`[Main] View with ID '${viewId}' not found.`);
    }

    if (activeButton) {
        activeButton.classList.add('active');
    } else {
         console.warn(`[Main] Nav button for view '${viewId}' not found.`);
    }

    // Optional: Add logic based on view (e.g., refresh list when switching to it)
    if (viewId === 'list') {
         // Maybe trigger a check for updates or just rely on sync
         // loadAndRenderSubscriptions(); // Careful: Avoid redundant calls
    }
}

// --- Core Application Logic ---

/**
 * Performs a full sync: gets local data, sends to API, processes response,
 * updates local DB, and re-renders UI.
 * @returns {Promise<boolean>} True if sync completed successfully, false otherwise.
 */
async function performFullSync() {
    if (!isUserLoggedIn()) {
        console.log('[Sync] User not logged in, full sync skipped.');
        return false;
    }
    console.log('[Sync] Starting full synchronization...');
    addChatMessage('正在与云端同步订阅数据...', 'system', null, true); // Add as transient message
    showCardsLoading(); // Show loading indicator on cards

    try {
        // 1. Get all local subscriptions
        const localSubscriptions = await getAllSubscriptions();
        console.log(`[Sync] Found ${localSubscriptions.length} items locally.`);

        // 2. Call the sync API
        const syncedSubscriptions = await syncSubscriptions(localSubscriptions);
        console.log(`[Sync] Received ${syncedSubscriptions.length} items from server.`);

        // 3. Clear local storage
        await clearAllSubscriptions(); 
        console.log('[Sync] Cleared local IndexedDB store.');

        // 4. Bulk add the synced subscriptions back
        if (syncedSubscriptions.length > 0) {
            await bulkAddOrUpdateSubscriptions(syncedSubscriptions);
            console.log('[Sync] Bulk added synced items to local IndexedDB store.');
        }

        // 5. Re-render the UI with the definitive list
        renderSubscriptionCards(syncedSubscriptions);
        console.log('[Sync] Rendered cards with synced data.');
        addChatMessage('数据同步完成。', 'system', null, true);
        return true; // Sync successful

    } catch (error) {
        console.error('[Sync] Full synchronization failed:', error);
        const errorMsg = `同步失败: ${error.message}`;
        addChatMessage(errorMsg, 'system');
        showCardsError(errorMsg); // Show error on cards section
        // Optional: Fallback to rendering potentially stale local data?
        // const staleLocalSubs = await getAllSubscriptions(); 
        // renderSubscriptionCards(staleLocalSubs);
        return false; // Sync failed
    }
}

/**
 * Loads subscriptions. On login, performs a full sync first.
 * Otherwise, just loads from local storage.
 * Renders the final list into the cards container.
 */
async function loadAndRenderSubscriptions() {
    console.log('[Main] Loading and rendering subscriptions...');
    // Use placeholders within the list view
    if (cardsLoadingPlaceholder) cardsLoadingPlaceholder.classList.remove('hidden');
    if (cardsErrorPlaceholder) cardsErrorPlaceholder.classList.add('hidden');
    if (cardsEmptyPlaceholder) cardsEmptyPlaceholder.classList.add('hidden');
    if (cardsContainer) cardsContainer.innerHTML = ''; // Clear previous cards

    let finalSubscriptionsToRender = [];

    try {
        if (isUserLoggedIn()) {
            console.log('[Main] User is logged in. Performing initial sync...');
            const syncSuccess = await performFullSync(); // performFullSync now handles rendering on success
            if (!syncSuccess) {
                console.warn('[Main] Initial sync failed. Loading local data as fallback.');
                finalSubscriptionsToRender = await getAllSubscriptions();
                renderSubscriptionCards(finalSubscriptionsToRender); // Render fallback data
            } else {
                // Sync successful, UI already rendered by performFullSync
                return; 
            }
        } else {
            console.log('[Main] User is logged out. Loading from local DB only...');
            finalSubscriptionsToRender = await getAllSubscriptions();
            renderSubscriptionCards(finalSubscriptionsToRender); // Render local data
        }

    } catch (error) {
        console.error('[Main] Error loading/rendering subscriptions:', error);
        const errorMsg = `加载订阅列表失败: ${error.message}`;
        // Display error in the placeholder
        if (cardsErrorPlaceholder) {
             cardsErrorPlaceholder.textContent = errorMsg;
             cardsErrorPlaceholder.classList.remove('hidden');
        }
        if (cardsLoadingPlaceholder) cardsLoadingPlaceholder.classList.add('hidden');
        addChatMessage(errorMsg, 'system'); // Also show in chat
    }
}

// --- Callback Handlers ---

async function handleUserLogin(session) {
    console.log('[Main] Handling user login...');
    disableChatInput(false, "在此输入订阅信息或描述...");
    await loadAndRenderSubscriptions(); // This now includes the initial sync
    startAutoSync();
}

async function handleUserLogout() {
    console.log('[Main] Handling user logout...');
    stopAutoSync(); // Stop background sync
    // Keep chat input enabled, just update the placeholder
    disableChatInput(false, "未登录：输入信息将本地保存");
    hidePartialEditForm(); // Hide form if open
    removePreviewImage(); // Clear image preview
    await loadAndRenderSubscriptions(); // Render local data only
    // Optional: Clear chat messages on logout?
    // document.getElementById('chat-messages').innerHTML = '';
    // addChatMessage('您已退出登录。', 'system');
}

/**
 * Processes a completed subscription result (received from API).
 * Saves it to storage, updates the UI, and switches to the list view.
 * @param {object} subscriptionData - The complete subscription data from the API.
 */
async function processCompleteSubscriptionResult(subscriptionData) {
    if (!subscriptionData || !subscriptionData.id) {
        console.error('[Main] Invalid subscription data received for processing:', subscriptionData);
        addChatMessage('处理完成的订阅时出错：数据无效。', 'system');
        return;
    }
    console.log(`[Main] Processing complete subscription: ${subscriptionData.name} (${subscriptionData.id})`);
    addChatMessage(`已成功添加/更新订阅：${subscriptionData.name}`, 'system');
    await saveAndDisplaySubscription(subscriptionData); // Save and update card UI
    
    // --- MODIFICATION: Switch to list view after successful save --- 
    switchToView('list'); 
    // --- END MODIFICATION ---
}

/**
 * Callback for handling user sending a new message (text or image).
 * Calls the appropriate parsing API and handles the response.
 */
async function handleSendMessage(text, imageFile) {
    console.log('[Main] Handling send message - START');
    // Keep the initial system message simple
    addChatMessage("正在处理您的请求...", 'system', null, true); 
    toggleSendButtonLoading(true); // Ensure loading state is active
    disableChatInput(true, "处理中..."); // Keep input disabled during API call

    let processingPromise = null;

    try {
        // --- INITIAL PARSE PATH --- 
        if (imageFile) {
            const formData = new FormData();
            formData.append('image', imageFile);
            if (text) formData.append('text', text); 
            console.log('[Main] Calling api.parseImage...');
            processingPromise = parseImage(formData);
        } else if (text) {
            console.log('[Main] Calling api.parseText...');
            processingPromise = parseText(text);
        } else {
            console.warn('[Main] handleSendMessage called with no text or image.');
            addChatMessage("请输入文本或上传图片后再试。", 'system');
            // Reset UI immediately if nothing to send
            toggleSendButtonLoading(false);
            disableChatInput(false); 
            return; 
        }

        console.log('[Main] Awaiting API response...');
        const result = await processingPromise; // result = { status, data?, conversationId?, question?, ... }
        console.log('[Main] API Call successful. Result:', result);

        // --- Process result based on status --- 
        if (result.status === 'complete') {
            console.log('[Main] Parsing complete on first try.');
            await processCompleteSubscriptionResult(result.data); // Use the helper function
            toggleConfirmationArea(false); // Ensure confirmation area is hidden

        } else if (result.status === 'awaiting_confirmation') {
            console.log('[Main] Awaiting confirmation...');
            if (!result.question || !result.conversationId) {
                 console.error('[Main] Awaiting confirmation status but missing question or conversationId.', result);
                 throw new Error('AI 请求确认，但缺少必要信息。');
            }
            addChatMessage(result.question, 'assistant');
            toggleConfirmationArea(true, result.conversationId); // Show confirmation buttons
            // Input remains disabled by toggleConfirmationArea

        } else if (result.status === 'incomplete') {
            console.log('[Main] Parsing incomplete, AI needs more info.');
            if (!result.question) {
                 console.error('[Main] Incomplete status but missing question.', result);
                 throw new Error('AI 需要更多信息，但未提供问题。');
            }
            addChatMessage(result.question, 'assistant');
            toggleConfirmationArea(false); // Ensure confirmation area is hidden

        } else {
            // Handle other unexpected statuses if necessary
            console.warn('[Main] Received unexpected status from parsing API:', result.status);
            addChatMessage(`收到意外状态：${result.status}。请重试。`, 'system');
            toggleConfirmationArea(false); // Ensure confirmation area is hidden
        }

    } catch (error) {
        console.error('[Main] Error during handleSendMessage:', error);
        addChatMessage(`处理您的请求时出错: ${error.message}`, 'system');
        toggleConfirmationArea(false); // Ensure confirmation area is hidden on error
    } finally {
         console.log('[Main] handleSendMessage - FINALLY block');
         // Re-enable input ONLY if NOT awaiting confirmation
         if (!isChatAwaitingConfirmation()) { 
              disableChatInput(false);
         }
         toggleSendButtonLoading(false);
    }
}

/**
 * Callback for handling user confirmation or correction during a conversation.
 * Passed to initChat as `onConfirm`.
 * @param {string} conversationId The ID of the conversation.
 * @param {boolean} confirmed True if the user clicked 'Yes', false if providing correction text.
 * @param {string | null} correctionText The correction text if confirmed is false.
 */
async function handleConfirm(conversationId, confirmed, correctionText) {
    console.log(`[Main] Handling confirmation for ${conversationId}. Confirmed: ${confirmed}`);
    addChatMessage('正在处理您的回应...', 'system', null, true);
    // Keep input disabled during this API call
    disableChatInput(true, "处理中..."); 
    toggleSendButtonLoading(true);

    try {
        const userReply = confirmed ? '确认' : correctionText; // Use standard confirmation text or correction
        if (!userReply) {
             throw new Error("无法发送空的回应。" ); // Should not happen based on chat.js logic
        }
        
        console.log(`[Main] Calling api.continueParse(${conversationId}, ...)...`);
        const result = await continueParse(conversationId, userReply); // API call
        console.log('[Main] continueParse API Call successful. Result:', result);

        // --- Process result --- 
        if (result.status === 'complete') {
            console.log('[Main] Confirmation complete.');
            await processCompleteSubscriptionResult(result.data); // Use helper
            toggleConfirmationArea(false); // Hide confirmation buttons

        } else if (result.status === 'awaiting_confirmation') {
            console.log('[Main] Re-awaiting confirmation after correction...');
            if (!result.question || !result.conversationId) {
                 console.error('[Main] Awaiting re-confirmation status but missing question or conversationId.', result);
                 throw new Error('AI 请求再次确认，但缺少必要信息。');
            }
            addChatMessage(result.question, 'assistant');
            toggleConfirmationArea(true, result.conversationId); // Show confirmation buttons again
            // Input remains disabled by toggleConfirmationArea

        } else if (result.status === 'incomplete') {
            console.log('[Main] Confirmation process incomplete, AI needs more info.');
            if (!result.question) {
                 console.error('[Main] Incomplete status during confirmation but missing question.', result);
                 throw new Error('AI 需要更多信息，但未提供问题。');
            }
            addChatMessage(result.question, 'assistant');
            toggleConfirmationArea(false); // Hide confirmation buttons

        } else {
            console.warn('[Main] Received unexpected status from continueParse API:', result.status);
            addChatMessage(`收到意外状态：${result.status}。请重试。`, 'system');
            toggleConfirmationArea(false); 
        }

    } catch (error) {
        console.error('[Main] Error during handleConfirm:', error);
        addChatMessage(`处理您的回应时出错: ${error.message}`, 'system');
        toggleConfirmationArea(false); // Ensure confirmation area is hidden on error
    } finally {
         console.log('[Main] handleConfirm - FINALLY block');
         // Re-enable input ONLY if NOT awaiting confirmation
         if (!isChatAwaitingConfirmation()) { 
              disableChatInput(false);
         }
         toggleSendButtonLoading(false);
    }
}

async function handleDeleteCard(id) {
    console.log(`[Main] Handling delete request for card ID: ${id}`);
    const isLocalOnlyId = id.startsWith('temp-'); // Check if the ID itself marks it as temporary
    const loggedIn = isUserLoggedIn(); // Check current login status

    try {
        if (loggedIn && !isLocalOnlyId) {
            // --- ONLINE DELETE --- 
            console.log(`[Main] User online, deleting from API and storage: ${id}`);
            await deleteSubscriptionApi(id); // Delete from backend first
            console.log(`[Main] Deleted item ${id} from API.`);
            await deleteSubscriptionStorage(id); // Then delete from local storage
            console.log(`[Main] Deleted item ${id} from storage.`);
        } else {
            // --- OFFLINE DELETE or TEMP ID DELETE ---
             if (!loggedIn) {
                 console.log(`[Main] User offline, deleting only from local storage: ${id}`);
             } else { // Logged in, but ID is temporary
                 console.log(`[Main] Deleting local-only temp item from storage: ${id}`);
             }
            await deleteSubscriptionStorage(id);
             console.log(`[Main] Deleted item ${id} from local storage.`);
        }
        // Remove from UI after successful deletion from relevant source(s)
        removeSubscriptionCard(id);
         console.log(`[Main] Removed card ${id} from UI.`);

    } catch (error) {
        console.error(`[Main] Failed to delete subscription ${id}:`, error);
        addChatMessage(`删除订阅 ${id} 失败: ${error.message}`, 'system');
        // Re-throw error so the card UI handler can catch it if needed 
        // (e.g., to re-enable the delete button)
        throw error; 
    }
}

async function handleReminderChange(id, isChecked) {
    console.log(`[Main] Handling reminder change for ${id} to ${isChecked}`);
    try {
        const updatedSubscription = await updateSubscription(id, { reminder: isChecked });
        // Update local storage with the confirmed data from the server
        await addOrUpdateSubscription(updatedSubscription);
        console.log(`[Main] Updated reminder for ${id} in storage.`);
        // Update the card in the UI to reflect any other changes from the PUT response
        updateSubscriptionCard(updatedSubscription);
    } catch (error) {
         console.error(`[Main] Failed to update reminder for ${id}:`, error);
         addChatMessage(`更新提醒失败: ${error.message}`, 'system');
         // Let the card UI revert the checkbox via its own error handling
         throw error; // Re-throw for card UI
    }
}

async function handleEditCard(id) {
    console.log(`[Main] Handling edit request for card ID: ${id}`);
    addChatMessage('编辑功能尚未实现。', 'system');
    // TODO: Implement edit logic
    // 1. Get subscription data (from storage?)
    // 2. Show a modal/form pre-filled with data
    // 3. On save, call updateSubscription API
    // 4. Update storage & UI
}

/**
 * Saves completed subscription data (from AI or form) to storage and updates UI.
 * Called by processCompleteSubscriptionResult.
 * @param {object} subscriptionData The complete subscription data.
 */
async function saveAndDisplaySubscription(subscriptionData) {
     console.log('[Main] Saving subscription data:', subscriptionData);
     try {
         await addOrUpdateSubscription(subscriptionData);
         console.log('[Main] Subscription saved to local DB:', subscriptionData.id);
         
         // Render the specific card - ensure cards module is aware of the container
         // The cards module (initCards) should handle adding/updating in the correct container
         const existingCard = cardsContainer?.querySelector(`.subscription-card[data-id="${subscriptionData.id}"]`);
         if (existingCard) {
            updateSubscriptionCard(subscriptionData); 
         } else {
             addSubscriptionCard(subscriptionData);
         }

         // Check if the list view is empty and hide/show the placeholder
         updateEmptyPlaceholderVisibility();

         if (isUserLoggedIn()) {
             console.log('[Main] User logged in, triggering background sync after save.');
             performFullSync().catch(err => {
                 console.error('[Main] Background sync after save failed:', err);
                 addChatMessage(`后台同步失败: ${err.message}`, 'system', null, true);
             });
         } else {
             addChatMessage('订阅已本地保存。登录后将同步到云端。', 'system', null, true);
         }
     } catch (error) {
         console.error('[Main] Error saving subscription:', error);
         addChatMessage(`保存订阅失败: ${error.message}`, 'system');
     }
 }

 /** Checks card container and shows/hides the empty placeholder */
 function updateEmptyPlaceholderVisibility() {
      if (cardsContainer && cardsEmptyPlaceholder) {
         const cardCount = cardsContainer.querySelectorAll('.subscription-card').length;
         if (cardCount === 0) {
             cardsEmptyPlaceholder.classList.remove('hidden');
         } else {
             cardsEmptyPlaceholder.classList.add('hidden');
         }
      }
 }

// --- Auto Sync --- //
function startAutoSync() {
  stopAutoSync(); // Clear any existing interval first
  const intervalMilliseconds = SYNC_INTERVAL_MINUTES * 60 * 1000;
  console.log(`[Main] Starting auto sync every ${SYNC_INTERVAL_MINUTES} minutes.`);

  syncIntervalId = setInterval(async () => {
    console.log("[AutoSync] Interval triggered. Checking login status...");
    if (isUserLoggedIn()) {
      console.log("[AutoSync] User logged in, attempting sync...");
      try {
        await performFullSync();
        console.log("[AutoSync] Sync attempt finished.");
      } catch (error) {
        console.error("[AutoSync] Error during scheduled sync:", error);
      }
    } else {
      console.log("[AutoSync] User not logged in, skipping sync.");
      stopAutoSync(); // Stop if user logged out unexpectedly
    }
  }, intervalMilliseconds);
}

function stopAutoSync() {
   if (syncIntervalId) {
       clearInterval(syncIntervalId);
       syncIntervalId = null;
       console.log("[Main] Auto sync stopped.");
   }
}


// --- Application Initialization ---
async function main() {
    console.log('[Main] DOMContentLoaded. Initializing application...');

    // --- Get DOM references --- 
    document.querySelectorAll('.nav-button').forEach(button => {
        const viewId = button.dataset.view;
        if (viewId) {
            navButtons[viewId] = button;
            views[viewId] = document.getElementById(`view-${viewId}`);
            // Add event listener for view switching
            button.addEventListener('click', () => switchToView(viewId));
        }
    });
    cardsContainer = document.getElementById('subscription-cards-container');
    cardsLoadingPlaceholder = document.getElementById('cards-loading-placeholder');
    cardsErrorPlaceholder = document.getElementById('cards-error-placeholder');
    cardsEmptyPlaceholder = document.getElementById('cards-empty-placeholder');
    chatMessagesElement = document.getElementById('chat-messages'); // Needed for addChatMessage checks maybe
    console.log('[Main] DOM references acquired.');

    // 1. Initialize Database first
    await initDB();
    console.log('[Main] Database initialized.');

    // 2. Initialize UI Components (pass callbacks and potentially container refs)
    // Ensure chat elements are found within #view-chat if initChat relies on specific IDs
    initChat({
        onSend: handleSendMessage,
        onConfirm: handleConfirm,
        isUserLoggedIn: isUserLoggedIn
    });
    // Pass the container reference to the cards module
    initCards({
        container: cardsContainer, // Pass the container element
        loadingPlaceholder: cardsLoadingPlaceholder,
        errorPlaceholder: cardsErrorPlaceholder,
        emptyPlaceholder: cardsEmptyPlaceholder,
        onDelete: handleDeleteCard,
        onEdit: handleEditCard,
        onReminderChange: handleReminderChange
    });
    // initForm may need adjustments or removal if not used in list view
    // initForm({ 
    //     onConfirm: handleConfirmPartialData, // This handler was removed
    //     onCancel: handleCancelPartialData // This handler was removed
    // });
    console.log('[Main] UI Modules initialized.');

    // 3. Initialize API Client 
    initApi({
        tokenProvider: getSessionToken
    });
    console.log('[Main] API Service initialized.');

    // 4. Initialize Authentication
    await initAuth({
        onLogin: handleUserLogin,
        onLogout: handleUserLogout
    });
    console.log('[Main] Authentication initialized.');

    // Add global listener for auth failures 
    window.addEventListener('auth-failure', (event) => {
         console.warn('[Main] Detected auth failure event:', event.detail);
         addChatMessage('您的登录已失效或需要重新登录。请检查您的登录状态。', 'system');
         stopAutoSync();
         renderSubscriptionCards([]); 
         updateEmptyPlaceholderVisibility(); // Show empty message
         disableChatInput(false, "登录失效，请重新登录后继续。");
         toggleConfirmationArea(false); 
         hidePartialEditForm(); 
         switchToView('chat'); // Switch back to chat/login prompt view
    });

    // 5. Initial Setup
    switchToView('chat'); // Start on the chat view
    // loadAndRenderSubscriptions() is now called by initAuth handlers
    // but call it once initially to load local data if not logged in
    if (!isUserLoggedIn()) {
        console.log('[Main] User not logged in, loading local data initially...');
        await loadAndRenderSubscriptions(); // Load and render local data immediately
    }
    
    console.log('[Main] Application started successfully.');
}

// --- Entry Point ---
window.addEventListener('load', main);
